import Vue from 'vue';
import $http from '../../shared/http';
import { vueTableDefaults } from '../../shared/gotedo_tables_defaults';
import { downloadFileFromResponse } from '../../shared/utils';

export const vue_tables = {
  namespaced: true,
  state: () => {
    return {
      tablePagination: null,
      selectedRows: [],
      actionProgressState: null,
      /**
       * Table defaults have been moved outside Vuex because of update deployment issues
       */
      tables: {},
      appendParams: {
        All_Users: {},
      },
      multiFilterQuery: {},
      tableCreateAcl: {},
    };
  },
  getters: {
    getActionProgressState: (state) => state.actionProgressState,
    getSelectedRows: (state) => {
      if (state.selectedRows && state.selectedRows.length) return state.selectedRows;
      else return [];
    },
    getTotalNumberOfRecords: (state) => state.tablePagination?.total,
    /**
     * @deprecated Use `getDefaultSortOrder`
     */
    getSortOrder: () => (table_name) => {
      return vueTableDefaults?.[table_name]?.sortOrder ?? {};
    },
    getDefaultSortOrder: () => (table_name) => {
      return vueTableDefaults?.[table_name]?.sortOrder;
    },
    getSearchFilter: (state) => (table_name) => {
      if (state.tables?.[table_name]?.filter?.length) {
        const searchFilters = state.tables[table_name].filter;
        const lastFilter = searchFilters.pop();
        return lastFilter;
      } else return {};
    },
    getTableRowsPerPage: (state) => (table_name) => {
      return state.tables?.[table_name]?.paginationData?.perPage ?? 20;
    },
    getTableCurrentPage: (state) => (table_name) => {
      return state.tables?.[table_name]?.paginationData?.currentPage ?? 1;
    },
    getTablePagination: (state) => {
      return state.tablePagination ?? null;
    },
    getLastViewedTableRow: (state) => (table_name) => {
      return state.tables?.[table_name]?.lastViewedTableRow ?? null;
    },
    GET_MULTI_FILTER_QUERY: (state) => (tableName) => state.multiFilterQuery?.[tableName] ?? null,
    GET_TABLE_CREATE_ACL: (state) => state.tableCreateAcl,
  },
  mutations: {
    setSelectedRows: (state, payload) => {
      state.selectedRows = payload;
    },
    addSelectedRow: (state, payload) => {
      if (payload) state.selectedRows.push(payload);
    },
    setTablePagination: (state, payload) => {
      state.tablePagination = payload;
    },
    setActionProgressState: (state, progressState) => {
      state.actionProgressState = progressState;
    },

    clearSearchFilter: (state, { tableName }) => {
      state.tables[tableName].filter = [];
    },
    /**
     *
     * @param {*} state
     * @param {keyof typeof vueTableDefaults} tableName
     */
    createTableEntry: (state, tableName) => {
      const defaults = vueTableDefaults[tableName];

      Vue.set(state.tables, tableName, {});
      Vue.set(state.tables[tableName], 'filter', []);
      Vue.set(state.tables[tableName], 'paginationData', {});
      Vue.set(
        state.tables[tableName]['paginationData'],
        'perPage',
        defaults?.paginationData?.perPage ?? 20
      );
    },

    setSearchFilter: (state, { tableName, searchName, searchText }) => {
      console.log(tableName, searchName, searchText);
      if (!state.tables[tableName]) state.tables[tableName] = {};
      if (!state.tables[tableName].filter) {
        // Avoid Vue's reactivity caveat
        Vue.set(state.tables[tableName], 'filter', []);
        state.tables[tableName].filter.push({ name: searchName, text: searchText });
      } else {
        const tableFilter = state.tables[tableName].filter;
        // check to see if that searchName already exists
        if (tableFilter && tableFilter.length) {
          tableFilter.forEach((filter, index) => {
            if (filter.name === searchName) {
              // Avoid Vue's reactivity caveat
              Vue.set(tableFilter, index, { name: searchName, text: searchText });
            } else tableFilter.push({ name: searchName, text: searchText });
          });
        } else {
          tableFilter.push({ name: searchName, text: searchText });
        }
      }
    },

    setPaginationData: (state, data) => {
      const { tableName, ...restOfData } = data;
      if (!state.tables[tableName]) {
        state.tables[tableName] = {};
      }
      // Avoid Vue's reactivity caveat
      Vue.set(state.tables[tableName], 'paginationData', restOfData);
    },

    setCurrentPage: (state, { tableName, currentPage }) => {
      if (!state.tables[tableName]) {
        state.tables[tableName] = {};
      }
      if (!state.tables[tableName]['paginationData']) {
        // Avoid Vue's reactivity caveat
        Vue.set(state.tables[tableName], 'paginationData', {
          perPage: 10,
          currentPage,
        });
      } else {
        Vue.set(state.tables[tableName]['paginationData'], 'currentPage', currentPage);
      }
    },

    setLastViewedTableRow: (state, { tableName, row }) => {
      if (!state.tables?.[tableName]) state.tables[tableName] = {};
      // Avoid Vue's reactivity caveat
      Vue.set(state.tables[tableName], 'lastViewedTableRow', row);
    },

    setTableCreateAcl: (state, payload) => {
      state.tableCreateAcl = payload;
    },
  },
  actions: {
    async deleteAction(ctx, { endpoint, id }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .delete(`${endpoint}/${id}`, { headers: { 'X-Is-Notifiable': 'On' } })
          .then((response) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      });
    },
    async customDeleteAction(ctx, { endpoint, id, endpointAppend }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .delete(`${endpoint}/${id}${endpointAppend ? '/' + endpointAppend : ''}`, {
            headers: { 'X-Is-Notifiable': 'On' },
          })
          .then((response) => {
            return resolve(response);
          })
          .catch((error) => {
            return reject(error);
          });
      });
    },
    async customGetAction(ctx, { endpoint, id, endpointAppend, customEndpointIsDownloadAction }) {
      return new Promise(async (resolve, reject) => {
        await $http(undefined, {
          forFileDownload: customEndpointIsDownloadAction ? true : undefined,
        })
          .get(`${endpoint}/${id}${endpointAppend ? '/' + endpointAppend : ''}`)
          .then(async (response) => {
            if (customEndpointIsDownloadAction) {
              await downloadFileFromResponse(response, 'file');
            }
            return resolve(response);
          })
          .catch((error) => {
            if (error && error.status === 429) {
              reject(error);
            }
            reject(error);
          });
      });
    },
    async customPostAction(ctx, { endpoint, id, endpointAppend }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .post(`${endpoint}/${id}${endpointAppend ? '/' + endpointAppend : ''}`, undefined, {
            headers: { 'X-Is-Notifiable': 'On' },
          })
          .then((response) => {
            return resolve(response);
          })
          .catch((error) => {
            if (error.status === 429) {
              reject(error);
            }
            reject(error);
          });
      });
    },
    async customPatchAction(ctx, { endpoint, id, endpointAppend }) {
      return new Promise(async (resolve, reject) => {
        await $http()
          .patch(`${endpoint}/${id}${endpointAppend ? '/' + endpointAppend : ''}`, undefined, {
            headers: { 'X-Is-Notifiable': 'On' },
          })
          .then((response) => {
            return resolve(response);
          })
          .catch((error) => {
            if (error.status === 429) {
              reject(error);
            }
            reject(error);
          });
      });
    },

    async FETCH_TABLE_DATA(
      { commit, rootGetters },
      { paginationParams, entityEndPoint, queryObject, isCompanySpecific }
    ) {
      const currentCompany = rootGetters['auth/GET_CURRENT_COMPANY'];

      return new Promise(async (resolve, reject) => {
        commit('SET_TABLE_DATA', []);

        await $http()
          .get(`${isCompanySpecific ? `${currentCompany.id}/${entityEndPoint}` : entityEndPoint}`, {
            params: paginationParams && queryObject ? { ...paginationParams, ...queryObject } : {},
          })
          .then((res) => {
            commit('SET_TABLE_DATA', res.data.data?.data ?? res.data.data);

            resolve(res.data);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
  },
};
